/**
 * EquipeConfig — Configuração genérica para o componente EquipeManager.
 * Define perfil, roles, rotas, labels e features para cada dashboard.
 * @internal true
 */
import type { LucideIcon } from 'lucide-react';
import { Shield, Users, ClipboardList, Eye, Mail, Building2, Globe, Network } from 'lucide-react';
import type { PermissionKey } from '../../auth/permissions';

// ═══════════════════════════════════════════════════════════════
// INTERFACES
// ═══════════════════════════════════════════════════════════════

export interface EquipeRole {
  id: string;
  label: string;
  description?: string;
  color: string;
  icon: LucideIcon;
  isDefault?: boolean;
}

export interface EquipeApiRoutes {
  listar: string;
  findOne: (id: string) => string;
  convidar: string;
  update: (id: string) => string;
  remove: (id: string) => string;
  reenviar: (id: string) => string;
  status: (id: string) => string;
}

export interface EquipeLabels {
  title: string;
  subtitle: string;
  inviteButton: string;
  emptyState: string;
  scope: string;
  cargoPlaceholder: string;
}

export interface EquipePermissionGroup {
  title: string;
  items: Array<{ key: PermissionKey; label: string }>;
}

export interface EquipeFeatures {
  permissions: boolean;
  ficha: boolean;
  statusControl: boolean;
  avatarUpload: boolean;
}

export interface EquipeConfig {
  /** Perfil do gestor */
  perfil: 'ESCOLA' | 'MUNICIPAL' | 'ESTADUAL' | 'REDE' | 'ADMIN';

  /** ID do escopo — preenchido no runtime (escolaId, municipioId, etc.) */
  scopeId: string;

  /** Campo de escopo para filtragem no backend */
  scopeField: 'escolaId' | 'municipioId' | 'estadoId' | 'redeId';

  /** Cargos disponíveis */
  roles: EquipeRole[];

  /** Rotas API */
  api: EquipeApiRoutes;

  /** Labels/textos */
  labels: EquipeLabels;

  /** Cor do hero/header */
  heroColor: string;

  /** Grupos de permissões para a aba de permissões */
  permissionGroups: EquipePermissionGroup[];

  /** Features habilitadas */
  features: EquipeFeatures;
}

// ═══════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════

/** Gera rotas API genéricas baseadas em perfil + scopeId */
function buildGenericApiRoutes(perfil: string, scopeId: string): EquipeApiRoutes {
  const base = `/equipe/${perfil}/${scopeId}/membros`;
  return {
    listar: base,
    findOne: (id: string) => `${base}/${id}`,
    convidar: `${base}/convidar`,
    update: (id: string) => `${base}/${id}`,
    remove: (id: string) => `${base}/${id}`,
    reenviar: (id: string) => `${base}/${id}/reenviar`,
    status: (id: string) => `${base}/${id}/status`,
  };
}

// ═══════════════════════════════════════════════════════════════
// PERMISSION GROUPS POR PERFIL
// ═══════════════════════════════════════════════════════════════

const ESCOLA_PERMISSION_GROUPS: EquipePermissionGroup[] = [
  {
    title: 'Equipe e Configuração',
    items: [
      { key: 'equipe.view', label: 'Visualizar equipe' },
      { key: 'equipe.manage', label: 'Gerenciar equipe' },
      { key: 'configuracoes.manage', label: 'Gerenciar configurações' },
    ],
  },
  {
    title: 'Acadêmico',
    items: [
      { key: 'turnos.view', label: 'Visualizar turnos' },
      { key: 'turnos.manage', label: 'Gerenciar turnos' },
      { key: 'materias.view', label: 'Visualizar matérias' },
      { key: 'materias.manage', label: 'Gerenciar matérias' },
      { key: 'turmas.view', label: 'Visualizar turmas' },
      { key: 'turmas.manage', label: 'Gerenciar turmas' },
    ],
  },
  {
    title: 'Pessoas e Contas',
    items: [
      { key: 'professores.view', label: 'Visualizar professores' },
      { key: 'professores.manage', label: 'Gerenciar professores' },
      { key: 'alunos.view', label: 'Visualizar alunos' },
      { key: 'alunos.manage', label: 'Gerenciar alunos' },
      { key: 'responsaveis.view', label: 'Visualizar responsáveis' },
      { key: 'responsaveis.manage', label: 'Gerenciar responsáveis' },
    ],
  },
  {
    title: 'Operacional',
    items: [
      { key: 'chamadas.view', label: 'Visualizar chamadas' },
      { key: 'chamadas.manage', label: 'Gerenciar chamadas' },
      { key: 'comunicados.view', label: 'Visualizar comunicados' },
      { key: 'comunicados.manage', label: 'Gerenciar comunicados' },
      { key: 'midias.view', label: 'Visualizar mídias' },
      { key: 'midias.manage', label: 'Gerenciar mídias' },
      { key: 'acesso.view', label: 'Visualizar acesso/portaria' },
      { key: 'acesso.manage', label: 'Gerenciar acesso/portaria' },
    ],
  },
  {
    title: 'Gestão e Infraestrutura',
    items: [
      { key: 'dashboard.view', label: 'Visualizar dashboard' },
      { key: 'relatorios.view', label: 'Visualizar relatórios' },
      { key: 'dispositivos.view', label: 'Visualizar dispositivos' },
      { key: 'dispositivos.manage', label: 'Gerenciar dispositivos' },
      { key: 'gestao.view', label: 'Visualizar dados de gestão' },
      { key: 'gestao.manage', label: 'Gerenciar dados de gestão' },
    ],
  },
];

const MUNICIPAL_PERMISSION_GROUPS: EquipePermissionGroup[] = [
  {
    title: 'Gestão Municipal',
    items: [
      { key: 'equipe.view', label: 'Visualizar equipe' },
      { key: 'equipe.manage', label: 'Gerenciar equipe' },
      { key: 'dashboard.view', label: 'Visualizar dashboard' },
      { key: 'relatorios.view', label: 'Visualizar relatórios' },
    ],
  },
  {
    title: 'Escolas e Comunicação',
    items: [
      { key: 'gestao.view', label: 'Visualizar escolas' },
      { key: 'gestao.manage', label: 'Gerenciar escolas' },
      { key: 'comunicados.view', label: 'Visualizar comunicados' },
      { key: 'comunicados.manage', label: 'Enviar comunicados' },
      { key: 'midias.view', label: 'Visualizar mídias' },
      { key: 'midias.manage', label: 'Gerenciar mídias' },
    ],
  },
];

const ESTADUAL_PERMISSION_GROUPS: EquipePermissionGroup[] = [
  {
    title: 'Gestão Estadual',
    items: [
      { key: 'equipe.view', label: 'Visualizar equipe' },
      { key: 'equipe.manage', label: 'Gerenciar equipe' },
      { key: 'dashboard.view', label: 'Visualizar dashboard' },
      { key: 'relatorios.view', label: 'Visualizar relatórios' },
    ],
  },
  {
    title: 'Municípios e Comunicação',
    items: [
      { key: 'gestao.view', label: 'Visualizar municípios' },
      { key: 'comunicados.view', label: 'Visualizar comunicados' },
      { key: 'comunicados.manage', label: 'Enviar comunicados' },
    ],
  },
];

const REDE_PERMISSION_GROUPS: EquipePermissionGroup[] = [
  {
    title: 'Gestão de Rede',
    items: [
      { key: 'equipe.view', label: 'Visualizar equipe' },
      { key: 'equipe.manage', label: 'Gerenciar equipe' },
      { key: 'dashboard.view', label: 'Visualizar dashboard' },
      { key: 'relatorios.view', label: 'Visualizar relatórios' },
    ],
  },
  {
    title: 'Abrangência',
    items: [
      { key: 'gestao.view', label: 'Visualizar estados e municípios' },
      { key: 'gestao.manage', label: 'Gerenciar abrangência' },
      { key: 'comunicados.view', label: 'Visualizar comunicados' },
      { key: 'comunicados.manage', label: 'Enviar comunicados' },
    ],
  },
];

// ═══════════════════════════════════════════════════════════════
// CONFIGS PRÉ-DEFINIDOS
// ═══════════════════════════════════════════════════════════════

export function buildEscolaConfig(escolaId: string): EquipeConfig {
  return {
    perfil: 'ESCOLA',
    scopeId: escolaId,
    scopeField: 'escolaId',
    roles: [
      { id: 'DIRETOR', label: 'Diretor(a)', description: 'Acesso total à escola. Pode gerenciar equipe, turnos, alunos e todas as configurações.', color: '#1e40af', icon: Shield },
      { id: 'VICE_DIRETOR', label: 'Vice Diretor', description: 'Acesso total à escola como vice-direção.', color: '#059669', icon: Shield },
      { id: 'COORDENACAO', label: 'Coordenação', description: 'Gerencia turmas, professores, matérias e acompanha o dia-a-dia pedagógico.', color: '#7c3aed', icon: Users },
      { id: 'SUPERVISOR', label: 'Supervisor(a)', description: 'Acompanha o desempenho acadêmico e pedagógico.', color: '#7c3aed', icon: Users },
      { id: 'SECRETARIA', label: 'Secretário(a)', description: 'Foco em matrículas, alunos, responsáveis, chamadas e documentação.', color: '#059669', icon: ClipboardList },
    ],
    api: buildGenericApiRoutes('ESCOLA', escolaId),
    labels: {
      title: 'Equipe Escolar',
      subtitle: 'Gerencie os membros da sua equipe escolar',
      inviteButton: 'Convidar Membro',
      emptyState: 'Nenhum membro cadastrado. Clique em "Convidar Membro" para começar.',
      scope: 'escola',
      cargoPlaceholder: 'Ex: Diretor(a), Coordenador(a), Secretário(a)...',
    },
    heroColor: '#1e40af',
    permissionGroups: ESCOLA_PERMISSION_GROUPS,
    features: { permissions: true, ficha: true, statusControl: true, avatarUpload: true },
  };
}

export function buildMunicipalConfig(municipioId: string): EquipeConfig {
  return {
    perfil: 'MUNICIPAL',
    scopeId: municipioId,
    scopeField: 'municipioId',
    roles: [
      { id: 'GESTOR', label: 'Gestor Municipal', description: 'Acesso total ao painel municipal. Visualiza escolas, envia comunicados e relatórios.', color: '#1e40af', icon: Shield, isDefault: true },
      { id: 'COMUNICACAO', label: 'Comunicação', description: 'Apenas comunicados e banco de mídias. Ideal para assessoria de comunicação.', color: '#7c3aed', icon: Mail },
      { id: 'CONSULTA', label: 'Consulta', description: 'Somente visualização. Pode ver escolas, dados e relatórios sem fazer alterações.', color: '#059669', icon: Eye },
    ],
    api: buildGenericApiRoutes('MUNICIPAL', municipioId),
    labels: {
      title: 'Equipe Municipal',
      subtitle: 'Gerencie os gestores da secretaria municipal de educação',
      inviteButton: 'Convidar Gestor',
      emptyState: 'Nenhum gestor cadastrado. Clique em "Convidar Gestor" para começar.',
      scope: 'prefeitura',
      cargoPlaceholder: 'Ex: Secretário de Educação, Assessor...',
    },
    heroColor: '#0369a1',
    permissionGroups: MUNICIPAL_PERMISSION_GROUPS,
    features: { permissions: true, ficha: true, statusControl: true, avatarUpload: true },
  };
}

export function buildEstadualConfig(estadoId: string): EquipeConfig {
  return {
    perfil: 'ESTADUAL',
    scopeId: estadoId,
    scopeField: 'estadoId',
    roles: [
      { id: 'GESTOR', label: 'Gestor Estadual', description: 'Acesso total ao painel estadual. Gerencia municípios e políticas educacionais.', color: '#1e40af', icon: Shield, isDefault: true },
      { id: 'ANALISTA', label: 'Analista', description: 'Acesso a relatórios, dados e indicadores. Sem permissão de escrita.', color: '#059669', icon: Eye },
      { id: 'COMUNICACAO', label: 'Comunicação', description: 'Gestão de comunicados oficiais do estado.', color: '#7c3aed', icon: Mail },
    ],
    api: buildGenericApiRoutes('ESTADUAL', estadoId),
    labels: {
      title: 'Equipe Estadual',
      subtitle: 'Gerencie a equipe da secretaria estadual de educação',
      inviteButton: 'Convidar Gestor',
      emptyState: 'Nenhum gestor estadual cadastrado.',
      scope: 'secretaria estadual',
      cargoPlaceholder: 'Ex: Superintendente, Analista, Assessor...',
    },
    heroColor: '#0f766e',
    permissionGroups: ESTADUAL_PERMISSION_GROUPS,
    features: { permissions: true, ficha: true, statusControl: true, avatarUpload: true },
  };
}

export function buildRedeConfig(redeId: string): EquipeConfig {
  return {
    perfil: 'REDE',
    scopeId: redeId,
    scopeField: 'redeId',
    roles: [
      { id: 'GESTOR', label: 'Gestor de Rede', description: 'Acesso total à gestão da rede escolar. Supervisiona estados e municípios.', color: '#1e40af', icon: Shield, isDefault: true },
      { id: 'SUPERVISOR', label: 'Supervisor', description: 'Acompanha indicadores e desempenho da rede.', color: '#7c3aed', icon: Network },
      { id: 'CONSULTA', label: 'Consulta', description: 'Apenas visualização de dados consolidados da rede.', color: '#059669', icon: Eye },
    ],
    api: buildGenericApiRoutes('REDE', redeId),
    labels: {
      title: 'Equipe da Rede',
      subtitle: 'Gerencie a equipe da rede escolar',
      inviteButton: 'Convidar Gestor',
      emptyState: 'Nenhum gestor da rede cadastrado.',
      scope: 'rede escolar',
      cargoPlaceholder: 'Ex: Gestor, Supervisor, Analista...',
    },
    heroColor: '#6d28d9',
    permissionGroups: REDE_PERMISSION_GROUPS,
    features: { permissions: true, ficha: true, statusControl: true, avatarUpload: true },
  };
}
